/**
 * ErvaPOS - Customer Product Orders Page
 * Encomendas de produtos para clientes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Search,
  Loader2,
  Package,
  Bell,
  CheckCircle,
  Clock,
  MoreVertical,
  Mail,
  MessageSquare,
  XCircle,
  Truck,
  User,
  Phone,
  Calendar,
  FileText,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const customerOrdersAPI = {
  getAll: (params = {}) => api.get('/customer-orders', { params }),
  getDashboard: () => api.get('/customer-orders/dashboard'),
  getById: (id) => api.get(`/customer-orders/${id}`),
  create: (data) => api.post('/customer-orders', data),
  update: (id, data) => api.put(`/customer-orders/${id}`, data),
  markArrived: (id) => api.put(`/customer-orders/${id}/mark-arrived`),
  notifyCustomer: (id, type = 'email', payload = {}) => api.put(`/customer-orders/${id}/notify-customer`, payload, { params: { notification_type: type } }),
  markCollected: (id) => api.put(`/customer-orders/${id}/mark-collected`),
  setOnlineStatus: (id, data) => api.put(`/customer-orders/${id}/online-status`, data),
  acknowledgeAutomation: (id) => api.put(`/customer-orders/${id}/acknowledge-automation`),
  createPayment: (id) => api.post(`/customer-orders/${id}/create-payment`),
  getReceipt: (id) => api.get(`/customer-orders/${id}/receipt`),
  getReceiptPDF: (id) => api.get(`/customer-orders/${id}/receipt-pdf`, {
    params: { format: 'receipt' },
    responseType: 'blob',
  }),
  cancel: (id) => api.delete(`/customer-orders/${id}`),
};

export const CustomerProductOrdersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [listView, setListView] = useState('active');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentNotifyForm, setPaymentNotifyForm] = useState({ phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [notifyingPayment, setNotifyingPayment] = useState(false);
  const [updatingOnlineOrder, setUpdatingOnlineOrder] = useState(null);

  const [productPickerQuery, setProductPickerQuery] = useState('');
  const [activeItemPickerIndex, setActiveItemPickerIndex] = useState(null);
  const [itemPickerQueries, setItemPickerQueries] = useState({});

  const emptyOrderItem = {
    product_id: '',
    product_name: '',
    quantity: 1,
    unit_price: 0,
    discount_percent: 0,
    notes: '',
  };

  const [orderForm, setOrderForm] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    items: [{ ...emptyOrderItem }],
    shipping_cost: 0,
    notes: '',
    estimated_arrival: '',

    payment_required: false,
    payment_method: '',
    payment_amount: '',
    payment_phone: '',
    payment_notes: '',
  });

  const todayDateValue = (() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  })();
  const maximumEstimatedArrival = (() => {
    const [year, month, day] = todayDateValue.split('-').map(Number);
    const targetMonthIndex = (month - 1) + 6;
    const targetYear = year + Math.floor(targetMonthIndex / 12);
    const targetMonth = targetMonthIndex % 12;
    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
    const result = new Date(targetYear, targetMonth, Math.min(day, lastDay));
    const local = new Date(result.getTime() - result.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  })();

  

  // ===== Picker de Cliente com Pesquisa (evita lista gigante) =====
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerPickerQuery, setCustomerPickerQuery] = useState('');

  const handleSendPaymentNotification = async (type) => {
    if (!selectedOrder?.id) return;

    const payload = {};
    if (type === 'sms') {
      const phone = String(paymentNotifyForm.phone || '').trim();
      if (!phone) {
        toast.error('Indica o telemóvel para enviar o SMS.');
        return;
      }
      payload.recipient_phone = phone;
    }

    if (type === 'email') {
      const email = String(paymentNotifyForm.email || '').trim();
      if (!email) {
        toast.error('Indica o email para enviar a mensagem.');
        return;
      }
      payload.recipient_email = email;
    }

    try {
      setNotifyingPayment(true);
      const res = await customerOrdersAPI.notifyCustomer(selectedOrder.id, type, payload);
      toast.success(type === 'sms' ? 'SMS enviado com sucesso.' : 'Email enviado com sucesso.');

      const ordersRes = await customerOrdersAPI.getAll();
      const allOrders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const freshOrder = allOrders.find((o) => o.id === selectedOrder.id) || {
        ...selectedOrder,
        notification_type: res?.data?.notification_type || type,
        notification_channels: res?.data?.notification_channels || selectedOrder?.notification_channels || [],
        sms_sent: type === 'sms' ? true : selectedOrder?.sms_sent,
        email_sent: type === 'email' ? true : selectedOrder?.email_sent,
        sms_sent_to: res?.data?.phone || selectedOrder?.sms_sent_to,
        email_sent_to: res?.data?.email || selectedOrder?.email_sent_to,
      };

      setOrders(allOrders);
      setSelectedOrder(freshOrder);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        `Falha ao enviar ${type === 'sms' ? 'SMS' : 'email'}.`;
      toast.error(msg);
    } finally {
      setNotifyingPayment(false);
    }
  };

  const norm = (v) => String(v || '').toLowerCase().trim();

  const customerMatches = (c, q) => {
    if (!q) return true;
    const qq = norm(q);
    return (
      norm(c?.name).includes(qq) ||
      norm(c?.phone).includes(qq) ||
      norm(c?.email).includes(qq) ||
      norm(c?.nif).includes(qq)
    );
  };

  const filteredCustomerOptions = customers
    .filter((c) => customerMatches(c, customerPickerQuery))
    .slice(0, 80); // hard cap para ficar rápido

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ordersRes, dashRes, prodsRes, custsRes] = await Promise.all([
        customerOrdersAPI.getAll({
          view: listView,
          status: filterStatus !== 'all' ? filterStatus : undefined,
        }),
        customerOrdersAPI.getDashboard(),
        api.get('/products'),
        api.get('/customers'),
      ]);
      setOrders(ordersRes.data || []);
      setDashboard(dashRes.data);
      setProducts(prodsRes.data || []);
      setCustomers(custsRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, listView]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  const filteredOrders = orders.filter(o => {
    if (!searchQuery) return true;
    return (
      o.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const selectCustomer = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setOrderForm(prev => ({
        ...prev,
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: customer.phone || '',
        customer_email: customer.email || '',
      }));
    }
  };

  const selectProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setOrderForm(prev => ({
        ...prev,
        product_id: product.id,
        product_name: product.name,
      }));
      setProductPickerQuery(product.name || '');
    }
  };


  const filteredProductOptions = (products || [])
    .filter((p) => p?.is_active !== false)
    .filter((p) => {
      const q = normalizeSearchText(productPickerQuery);
      if (!q) return true;
      return (
        normalizeSearchText(p.name).includes(q) ||
        normalizeSearchText(p.reference).includes(q) ||
        normalizeSearchText(p.barcode).includes(q) ||
        normalizeSearchText(p.category).includes(q)
      );
    })
    .slice(0, 30);


  const updateOrderItem = (index, patch) => {
    setOrderForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const addOrderItem = () => {
    setOrderForm((prev) => ({
      ...prev,
      items: [...(prev.items || []), { ...emptyOrderItem }],
    }));
    setActiveItemPickerIndex((prev) => prev === null ? 0 : prev);
  };

  const removeOrderItem = (index) => {
    setOrderForm((prev) => {
      const next = (prev.items || []).filter((_, i) => i !== index);
      return {
        ...prev,
        items: next.length ? next : [{ ...emptyOrderItem }],
      };
    });
    setItemPickerQueries((prev) => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
    setActiveItemPickerIndex((prev) => prev === index ? null : prev);
  };

  const selectProductForOrderItem = (index, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    updateOrderItem(index, {
      product_id: p.id,
      product_name: p.name || '',
      unit_price: Number(p.sell_price || 0),
    });
    setItemPickerQueries((prev) => ({ ...prev, [index]: p.name || '' }));
    setActiveItemPickerIndex(null);
  };

  const getFilteredProductOptionsForItem = (index) => {
    const q = normalizeSearchText(itemPickerQueries[index] || '');
    return (products || [])
      .filter((p) => p?.is_active !== false)
      .filter((p) => {
        if (!q) return true;
        return (
          normalizeSearchText(p.name).includes(q) ||
          normalizeSearchText(p.reference).includes(q) ||
          normalizeSearchText(p.barcode).includes(q) ||
          normalizeSearchText(p.category).includes(q)
        );
      })
      .slice(0, 20);
  };

  const parsePtNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const s = String(value).trim().replace(',', '.');
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const orderTotals = (() => {
    const items = Array.isArray(orderForm.items) ? orderForm.items : [];
    const normalizedItems = items.map((item) => {
      const quantity = Math.max(1, parseInt(item.quantity || 1, 10) || 1);
      const unitPrice = parsePtNumber(item.unit_price || 0);
      const discountPercent = Math.max(0, Math.min(100, parsePtNumber(item.discount_percent || 0)));
      const gross = quantity * unitPrice;
      const discountAmount = gross * (discountPercent / 100);
      const lineSubtotal = gross - discountAmount;
      return {
        ...item,
        quantity,
        unit_price: unitPrice,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        line_subtotal: lineSubtotal,
      };
    });
    const subtotal = normalizedItems.reduce((sum, item) => sum + Number(item.line_subtotal || 0), 0);
    const discountTotal = normalizedItems.reduce((sum, item) => sum + Number(item.discount_amount || 0), 0);
    const shippingCost = parsePtNumber(orderForm.shipping_cost || 0);
    const total = subtotal + shippingCost;
    return { items: normalizedItems, subtotal, discountTotal, shippingCost, total };
  })();

  const createOrder = async () => {
    const validItems = orderTotals.items.filter((item) => String(item.product_name || '').trim());

    if (!orderForm.customer_name || validItems.length === 0) {
      toast.error('Preencha cliente e pelo menos um produto');
      return;
    }

    if (orderForm.estimated_arrival && orderForm.estimated_arrival < todayDateValue) {
      toast.error('A data prevista não pode ser anterior à data atual');
      return;
    }

    if (orderForm.estimated_arrival && orderForm.estimated_arrival > maximumEstimatedArrival) {
      toast.error('A data prevista não pode ser superior a seis meses');
      return;
    }

    if (orderForm.payment_required && !orderForm.payment_method) {
      toast.error('Selecione o método de pré-pagamento');
      return;
    }

    if (orderForm.payment_required && Number(orderTotals.total || 0) <= 0) {
      toast.error('O total da encomenda tem de ser superior a zero');
      return;
    }

    if (orderForm.payment_required && orderForm.payment_method === 'mbway' && !orderForm.payment_phone) {
      toast.error('Indique o telefone para MB WAY');
      return;
    }

    setSaving(true);
    try {
      const normalizedItems = validItems.map((item) => ({
        product_id: item.product_id || '',
        product_name: item.product_name || '',
        quantity: parseInt(item.quantity || 1, 10) || 1,
        unit_price: parsePtNumber(item.unit_price || 0),
        discount_percent: parsePtNumber(item.discount_percent || 0),
        notes: item.notes || '',
      }));

      const firstItem = normalizedItems[0] || {};
      const totalQuantity = normalizedItems.reduce((sum, item) => sum + (parseInt(item.quantity || 0, 10) || 0), 0);

      const totalAmount = parsePtNumber(orderTotals.total || 0);
      const paymentMethod = orderForm.payment_required ? String(orderForm.payment_method || '').trim().toLowerCase() : '';
      const manualPaidAmountRaw = parsePtNumber(orderForm.payment_amount || 0);

      const isAutomaticIfthenpayMethod = ['mbway', 'multibanco'].includes(paymentMethod);

      const paidAmount = !orderForm.payment_required
        ? 0
        : isAutomaticIfthenpayMethod
          ? 0
          : Math.max(0, manualPaidAmountRaw);

      const normalizedPaidAmount = Math.min(totalAmount, paidAmount);

      const paymentStatus = !orderForm.payment_required
        ? 'pending'
        : isAutomaticIfthenpayMethod
          ? 'pending'
          : normalizedPaidAmount >= totalAmount && totalAmount > 0
            ? 'paid'
            : normalizedPaidAmount > 0
              ? 'partial'
              : 'pending';

      const payload = {
        ...orderForm,

        product_id: firstItem.product_id || '',
        product_name: firstItem.product_name || '',
        quantity: totalQuantity || 1,

        items: normalizedItems,
        shipping_cost: parsePtNumber(orderForm.shipping_cost || 0),
        subtotal_amount: parsePtNumber(orderTotals.subtotal || 0),
        discount_total: parsePtNumber(orderTotals.discountTotal || 0),
        total_amount: totalAmount,

        payment_required: !!orderForm.payment_required,
        payment_method: orderForm.payment_required ? paymentMethod : '',
        payment_amount: orderForm.payment_required ? totalAmount : 0,
        payment_paid_amount: orderForm.payment_required ? normalizedPaidAmount : 0,
        is_prepaid: !!(orderForm.payment_required && normalizedPaidAmount > 0),
        payment_status: paymentStatus,
        payment_phone: orderForm.payment_required ? (orderForm.payment_phone || '') : '',
        payment_notes: orderForm.payment_required ? (orderForm.payment_notes || '') : '',
      };

      await customerOrdersAPI.create(payload);
      toast.success('Encomenda criada');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao criar encomenda');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setOrderForm({
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      items: [{ ...emptyOrderItem }],
      shipping_cost: 0,
      notes: '',
      estimated_arrival: '',
      payment_required: false,
      payment_method: '',
      payment_amount: '',
      payment_phone: '',
      payment_notes: '',
    });
    setProductPickerQuery('');
    setItemPickerQueries({});
    setActiveItemPickerIndex(null);
  };

  const handleOpenInPOS = async (order) => {
    if (!order?.id) return;

    if (String(order.status || '').toLowerCase() === 'cancelled') {
      toast.error('A encomenda está cancelada.');
      return;
    }

    if (order.converted_to_sale) {
      toast.error('Esta encomenda já foi convertida em venda.');
      return;
    }

    sessionStorage.setItem('convertingOrderId', order.id);
    sessionStorage.removeItem('customerOrderAutoOpenDone');
    toast.success('A abrir encomenda no POS...');
    navigate('/pos');
  };

  const handlePrintCustomerOrder = async (order) => {
    try {
      const res = await customerOrdersAPI.getReceiptPDF(order.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error('Erro ao gerar talão da encomenda');
    }
  };

  const handlePrintArticleReservationSlip = (order) => {
    try {
      const esc = (value) =>
        String(value || '—')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const orderDate = order?.created_at
        ? new Date(order.created_at).toLocaleDateString('pt-PT')
        : '—';

      const paymentMethodMap = {
        cash: 'Numerário',
        card: 'Cartão',
        mbway: 'MB WAY',
        multibanco: 'Multibanco',
        transfer: 'Transferência',
        reference: 'Referência',
        other: 'Outro',
      };

      const paymentMethod = order?.payment_required
        ? (paymentMethodMap[String(order?.payment_method || '').toLowerCase()] || order?.payment_method || 'Definir')
        : 'Sem pré-pagamento';

      const paymentStatusMap = {
        pending: 'Pendente',
        paid: 'Pago',
        partial: 'Parcial',
        failed: 'Falhado',
        not_required: 'Não aplicável',
      };

      const paymentStatus = order?.payment_required
        ? (paymentStatusMap[String(order?.payment_status || '').toLowerCase()] || order?.payment_status || 'Pendente')
        : 'Não aplicável';

      const estimatedArrival = order?.estimated_arrival
        ? esc(order.estimated_arrival)
        : '—';

      const phone = order?.customer_phone ? esc(order.customer_phone) : '—';

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Talão Reserva</title>
  <style>
    @page {
      size: 80mm auto;
      margin: 0;
    }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      font-family: Arial, Helvetica, sans-serif;
      width: 80mm;
      min-height: 1mm;
      overflow: visible;
    }
    body {
      width: 80mm;
      padding: 3mm 2mm 8mm 2mm;
      font-size: 8.5pt;
      line-height: 1.15;
    }
    .wrap {
      width: 76mm;
      border: 1px dashed #999;
      padding: 2.5mm;
      overflow: visible;
    }
    .title {
      text-align: center;
      font-size: 7pt;
      font-weight: 700;
      letter-spacing: 0.3px;
      margin-bottom: 1.2mm;
    }
    .customer {
      font-size: 11pt;
      font-weight: 800;
      text-transform: uppercase;
      text-align: center;
      margin: 0.6mm 0 1.5mm 0;
      word-break: break-word;
      line-height: 1.05;
    }
    .row {
      display: flex;
      gap: 1.5mm;
      margin: 0.5mm 0;
      align-items: flex-start;
    }
    .label {
      width: 21mm;
      font-weight: 700;
      font-size: 7.5pt;
    }
    .value {
      flex: 1;
      word-break: break-word;
      font-size: 7.5pt;
    }
    .phone {
      margin-top: 1.2mm;
      padding-top: 1mm;
      border-top: 1px solid #ccc;
      text-align: center;
      font-size: 11pt;
      font-weight: 800;
      line-height: 1.05;
      word-break: break-word;
    }
    @media print {
      html, body {
        width: 80mm;
        min-height: 1mm;
        overflow: visible;
      }
      body {
        width: 80mm;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="title">RESERVA CLIENTE</div>
    <div class="customer">${esc(order?.customer_name || 'Cliente')}</div>

    <div class="row"><div class="label">Encomenda</div><div class="value">${esc(order?.order_number || '—')}</div></div>
    <div class="row"><div class="label">Data</div><div class="value">${esc(orderDate)}</div></div>
    <div class="row"><div class="label">Pagamento</div><div class="value">${esc(paymentMethod)}</div></div>
    <div class="row"><div class="label">Estado</div><div class="value">${esc(paymentStatus)}</div></div>
    <div class="row"><div class="label">Entrega</div><div class="value">${estimatedArrival}</div></div>

    <div class="phone">Tel: ${phone}</div>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 250);
    };
  </script>
</body>
</html>`;

      const win = window.open('', '_blank', 'width=420,height=700');
      if (!win) {
        toast.error('O navegador bloqueou a janela de impressão');
        return;
      }

      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (error) {
      toast.error('Erro ao gerar talão do artigo reservado');
    }
  };

  const handleMarkArrived = async (order) => {
    try {
      await customerOrdersAPI.markArrived(order.id);
      toast.success('Produto marcado como chegado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleNotifyCustomer = async (notificationType) => {
    if (!selectedOrder) return;
    setSaving(true);
    try {
      const response = await customerOrdersAPI.notifyCustomer(selectedOrder.id, notificationType);
      toast.success(response.data.message);
      setShowNotifyModal(false);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao notificar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkCollected = async (order) => {
    try {
      await customerOrdersAPI.markCollected(order.id);
      toast.success('Encomenda marcada como levantada');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleOnlineStatus = async (order, status) => {
    const data = { status };
    if (status === 'shipped') {
      data.carrier = window.prompt('Transportadora', order.carrier || 'GLS') || '';
      data.tracking_number = window.prompt('Número de seguimento', order.tracking_number || '') || '';
      data.tracking_url = window.prompt('Ligação de seguimento (opcional)', order.tracking_url || '') || '';
      if (!data.carrier || !data.tracking_number) return;
    }
    setUpdatingOnlineOrder(order.id);
    try {
      await customerOrdersAPI.setOnlineStatus(order.id, data);
      toast.success('Estado atualizado na NaturOk e cliente notificado');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível atualizar a encomenda online');
    } finally { setUpdatingOnlineOrder(null); }
  };

  const handleCancel = async (order) => {
    if (!window.confirm('Tem a certeza que deseja cancelar esta encomenda?')) return;
    try {
      await customerOrdersAPI.cancel(order.id);
      toast.success('Encomenda cancelada');
      loadData();
    } catch (error) {
      toast.error('Erro ao cancelar');
    }
  };

  const acknowledgeAutomation = async (order) => {
    if (!order?.automation_attention || order?.automation_viewed_at) return;
    setOrders(prev => prev.map(item => item.id === order.id ? { ...item, automation_attention: false, automation_viewed_at: new Date().toISOString() } : item));
    try {
      await customerOrdersAPI.acknowledgeAutomation(order.id);
      window.dispatchEvent(new CustomEvent('customer-order-alerts-changed'));
    } catch {
      loadData();
    }
  };

  const getNotificationChannelLabel = (order) => {
    const channels = order?.notification_channels || [];
    const type = order?.notification_type || '';

    if (channels.includes('email') && channels.includes('sms')) return 'Email + SMS';
    if (channels.includes('sms')) return 'SMS';
    if (channels.includes('email')) return 'Email';
    if (type === 'multiple') return 'Email + SMS';
    if (type === 'sms') return 'SMS';
    if (type === 'email') return 'Email';
    return '—';
  };

  const getNotificationBadge = (order) => {
    const label = getNotificationChannelLabel(order);
    if (label === 'SMS') return <Badge className="bg-blue-600">SMS</Badge>;
    if (label === 'Email') return <Badge className="bg-emerald-600">Email</Badge>;
    if (label === 'Email + SMS') return <Badge className="bg-purple-600">Email + SMS</Badge>;
    return <Badge className="bg-slate-600">—</Badge>;
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('pt-PT');
    } catch {
      return value;
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString('pt-PT');
    } catch {
      return value;
    }
  };

  const getStatusBadge = (status, order = null) => {
    const attention = (order?.automation_attention && !order?.automation_viewed_at) || order?.pickup_overdue;
    const pulseClass = attention ? 'animate-pulse ring-2 ring-amber-300 shadow-lg shadow-amber-500/50' : '';
    switch (status) {
      case 'pending':
        return <Badge className={`badge-warning ${pulseClass}`}>Aguarda Produto</Badge>;
      case 'arrived':
        return <Badge className={`badge-info ${pulseClass}`}>Produto Chegou</Badge>;
      case 'notified':
        return <Badge className={`${order?.pickup_overdue ? 'bg-rose-600' : 'badge-success'} ${pulseClass}`}>{order?.pickup_overdue ? 'Levantamento atrasado' : 'Cliente Notificado'}</Badge>;
      case 'collected':
        return <Badge className="bg-emerald-600">Levantado</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-600">Cancelada</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };


  const getOrderPaidAmount = (order) => {
    const paid = Number(order?.payment_paid_amount || 0);
    if (paid > 0) return paid;

    const status = String(order?.payment_status || '').toLowerCase();
    const total = Number(order?.payment_amount || order?.total_amount || 0);

    if (status === 'paid' && total > 0) return total;
    return 0;
  };

  const getDerivedPaymentStatus = (order) => {
    if (!order?.payment_required) return 'none';

    const total = Number(order?.payment_amount || order?.total_amount || 0);
    const paid = getOrderPaidAmount(order);
    const raw = String(order?.payment_status || '').toLowerCase();

    if (paid >= total && total > 0) return 'paid';
    if (paid > 0) return 'partial';
    if (raw === 'failed') return 'failed';
    if (raw === 'cancelled') return 'cancelled';
    return 'pending';
  };

  const getPaymentInfoLabel = (order) => {
    if (!order?.payment_required) return 'Sem pré-pagamento';

    const method = String(order?.payment_method || '—').toUpperCase();
    const total = Number(order?.payment_amount || order?.total_amount || 0);
    const paid = getOrderPaidAmount(order);
    const status = getDerivedPaymentStatus(order);

    if (status === 'paid') return `${method} · €${paid.toFixed(2)}`;
    if (status === 'partial') return `${method} · €${paid.toFixed(2)}`;
    return `${method} · €${total.toFixed(2)}`;
  };

  const getPaymentBadge = (order) => {
    const status = getDerivedPaymentStatus(order);

    if (status === 'none') {
      return <Badge className="bg-slate-700">Sem pré-pagamento</Badge>;
    }

    switch (status) {
      case 'paid':
        return <Badge className="bg-emerald-600">Pago</Badge>;
      case 'partial':
        return <Badge className="bg-amber-600">Parcial</Badge>;
      case 'failed':
        return <Badge className="bg-rose-600">Falhado</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-600">Cancelado</Badge>;
      default:
        return <Badge className="bg-amber-600">Pendente</Badge>;
    }
  };

  const handleCreatePayment = async (order) => {
    try {
      setSaving(true);
      const res = await customerOrdersAPI.createPayment(order.id);
      const updatedOrder = res.data?.order || order;
      setPaymentResult(res.data);
      setSelectedOrder(updatedOrder);
      setPaymentNotifyForm({
        phone: updatedOrder.payment_phone || updatedOrder.customer_phone || '',
        email: updatedOrder.customer_email || '',
      });
      setShowPaymentModal(true);
      setOrders((prev) => prev.map((o) => (o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o)));
      toast.success('Pagamento gerado');
      await loadData();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erro ao gerar pagamento';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPaymentData = async (order) => {
    try {
      setSaving(true);
      const ordersRes = await customerOrdersAPI.getAll();
      const allOrders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const freshOrder = allOrders.find((o) => o.id === order.id) || order;
      setOrders(allOrders);
      setSelectedOrder(freshOrder);
      setPaymentNotifyForm({
        phone: freshOrder.payment_phone || freshOrder.customer_phone || '',
        email: freshOrder.customer_email || '',
      });
      setShowPaymentModal(true);
    } catch (error) {
      setSelectedOrder(order);
      setPaymentNotifyForm({
        phone: order.payment_phone || order.customer_phone || '',
        email: order.customer_email || '',
      });
      setShowPaymentModal(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Encomendas/Reservas</h1>
          <p className="text-slate-400">Gestão de encomendas e reservas de clientes</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-500 hover:bg-emerald-600"
          data-testid="new-customer-order-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Encomenda/Reserva
        </Button>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <Card className="card-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Pendentes</p>
                <p className="text-xl font-bold text-white">{dashboard.pending}</p>
              </div>
            </div>
          </Card>
          <Card className="card-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Chegaram</p>
                <p className="text-xl font-bold text-white">{dashboard.arrived}</p>
              </div>
            </div>
          </Card>
          <Card className="card-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">A Notificar</p>
                <p className="text-xl font-bold text-white">{dashboard.awaiting_notification}</p>
              </div>
            </div>
          </Card>
          <Card className="card-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Levantadas</p>
                <p className="text-xl font-bold text-white">{dashboard.collected}</p>
              </div>
            </div>
          </Card>
          <Card className="card-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-500/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Total</p>
                <p className="text-xl font-bold text-white">{dashboard.total}</p>
              </div>
            </div>
          </Card>
          <Card className={`card-dark p-4 ${Number(dashboard.overdue_pickup || 0) > 0 ? 'border border-rose-500/60 bg-rose-950/20' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                <Bell className={`w-5 h-5 text-rose-400 ${Number(dashboard.overdue_pickup || 0) > 0 ? 'animate-pulse' : ''}`} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Levantamento atrasado</p>
                <p className="text-xl font-bold text-white">{dashboard.overdue_pickup || 0}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-slate-700 pb-3">
        <Button type="button" onClick={() => { setListView('active'); setFilterStatus('all'); }} className={listView === 'active' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}>
          <Package className="w-4 h-4 mr-2" /> Em processamento
        </Button>
        <Button type="button" onClick={() => { setListView('history'); setFilterStatus('all'); }} className={listView === 'history' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}>
          <Clock className="w-4 h-4 mr-2" /> Histórico ({Number(dashboard?.collected || 0) + Number(dashboard?.cancelled || 0)})
        </Button>
        <Button type="button" onClick={() => { setListView('online'); setFilterStatus('all'); }} className={listView === 'online' ? 'bg-orange-600 hover:bg-orange-500' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}>
          <Truck className="w-4 h-4 mr-2" /> Vendas online NaturOk
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Pesquisar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {listView === 'online' ? null : listView === 'active' ? <>
              <SelectItem value="pending">Aguarda Produto</SelectItem>
              <SelectItem value="arrived">Produto Chegou</SelectItem>
              <SelectItem value="notified">Cliente Notificado</SelectItem>
            </> : <>
              <SelectItem value="collected">Levantadas</SelectItem>
              <SelectItem value="cancelled">Canceladas</SelectItem>
            </>}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card className="card-dark">
        <Table className="table-dark">
          <TableHeader>
            <TableRow>
              <TableHead>Nº Encomenda</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Produto</TableHead>
              <TableHead>Qtd</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Pagamento</TableHead>
              <TableHead>Notificação</TableHead>
              <TableHead>Previsão</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrders.map((order) => (
              <TableRow key={order.id} onClick={() => acknowledgeAutomation(order)} className={order.pickup_overdue ? 'cursor-pointer border-l-4 border-l-rose-500 bg-rose-500/10' : order.automation_attention && !order.automation_viewed_at ? 'cursor-pointer border-l-4 border-l-amber-400 bg-amber-500/10' : ''}>
                <TableCell>
                  <div className="space-y-1.5">
                    <code className="inline-block px-2 py-1 bg-slate-800 rounded text-emerald-400 font-mono text-xs">
                      {order.order_number}
                    </code>
                    <p className="text-[11px] text-slate-500">
                      Criada em {formatDate(order.created_at)}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-white">{order.customer_name}</p>
                    <div className="flex gap-2 text-xs text-slate-500">
                      {order.customer_phone && <span>{order.customer_phone}</span>}
                      {order.customer_email && <span>{order.customer_email}</span>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-white">{order.product_name}</TableCell>
                <TableCell className="text-slate-300">{order.quantity}</TableCell>
                <TableCell>
                  {getStatusBadge(order.status, order)}
                  {order.is_online_order && <p className="mt-1 text-[10px] font-semibold text-orange-300">NaturOk · {order.online_status || 'received'}</p>}
                  {order.pickup_overdue && <p className="mt-1 text-[10px] font-semibold text-rose-300 animate-pulse">Por levantar há {order.pickup_wait_days} dias</p>}
                  {order.automation_attention && !order.automation_viewed_at && <p className="mt-1 text-[10px] font-semibold text-amber-300 animate-pulse">Alteração automática — clique para confirmar leitura</p>}
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div>{getPaymentBadge(order)}</div>
                    {order.payment_required && (
                      <p className="text-[11px] text-slate-500">
                        {getPaymentInfoLabel(order)}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div>{getNotificationBadge(order)}</div>
                    {order.notification_date && (
                      <p className="text-[11px] text-slate-500">
                        {formatDateTime(order.notification_date)}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-slate-400">
                  {order.estimated_arrival || '-'}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {order.is_online_order && <>
                        <DropdownMenuItem disabled={updatingOnlineOrder === order.id} onClick={() => handleOnlineStatus(order, 'preparing')}>Em preparação</DropdownMenuItem>
                        <DropdownMenuItem disabled={updatingOnlineOrder === order.id} onClick={() => handleOnlineStatus(order, 'shipped')}>Entregue à transportadora</DropdownMenuItem>
                        <DropdownMenuItem disabled={updatingOnlineOrder === order.id} onClick={() => handleOnlineStatus(order, 'completed')}>Concluir encomenda</DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>}
                      {order.payment_required && order.payment_status !== 'paid' && ['mbway', 'multibanco'].includes(order.payment_method) && (
                        <DropdownMenuItem onClick={() => handleCreatePayment(order)}>
                          <FileText className="w-4 h-4 mr-2" />
                          Gerar pagamento
                        </DropdownMenuItem>
                      )}

                      {order.payment_required && ['mbway', 'multibanco'].includes((order.payment_method || '').toLowerCase()) && (order.payment_request_id || order.payment_reference || order.payment_reference_entity || order.payment_callback_payload) && (
                        <DropdownMenuItem onClick={() => handleOpenPaymentData(order)}>
                          <FileText className="w-4 h-4 mr-2" />
                          Ver dados de pagamento
                        </DropdownMenuItem>
                      )}

                      {order.status === 'pending' && (
                        <DropdownMenuItem onClick={() => handleMarkArrived(order)}>
                          <Truck className="w-4 h-4 mr-2" />
                          Marcar Chegou
                        </DropdownMenuItem>
                      )}
                      {(order.status === 'arrived' || order.status === 'notified' || (order.status === 'pending' && !order.payment_required)) && (
                        <DropdownMenuItem onClick={() => { setSelectedOrder(order); setShowNotifyModal(true); }}>
                          <Bell className="w-4 h-4 mr-2" />
                          Notificar Cliente
                        </DropdownMenuItem>
                      )}
                      {order.status === 'notified' && (
                        <DropdownMenuItem onClick={() => handleMarkCollected(order)}>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Marcar Levantado
                        </DropdownMenuItem>
                      )}
                      {!order.converted_to_sale && order.status !== 'cancelled' && (
                        <DropdownMenuItem onClick={() => handleOpenInPOS(order)}>
                          <Package className="w-4 h-4 mr-2" />
                          Abrir no POS
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handlePrintCustomerOrder(order)}>
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimir Talão
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handlePrintArticleReservationSlip(order)}>
                        <FileText className="w-4 h-4 mr-2" />
                        Imprimir Talão p/ Artigo
                      </DropdownMenuItem>

                      {order.status !== 'collected' && order.status !== 'cancelled' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleCancel(order)} className="text-rose-400">
                            <XCircle className="w-4 h-4 mr-2" />
                            Cancelar
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma encomenda encontrada</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Order Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Nova Encomenda/Reserva</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              
<div>
  <Label className="text-slate-300">Cliente</Label>

  <Button
    type="button"
    variant="outline"
    className="w-full justify-start bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
    onClick={() => {
      setCustomerPickerQuery('');
      setCustomerPickerOpen(true);
    }}
  >
    <User className="w-4 h-4 mr-2 text-slate-400" />
    {orderForm.customer_id && orderForm.customer_name
      ? orderForm.customer_name
      : 'Selecionar cliente...'}
  </Button>

  <p className="text-xs text-slate-500 mt-2">
    Escreve para pesquisar por nome, telefone, email ou NIF.
  </p>

  <Dialog open={customerPickerOpen} onOpenChange={setCustomerPickerOpen}>
    <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
      <DialogHeader>
        <DialogTitle className="text-white">Escolher cliente</DialogTitle>
      </DialogHeader>

      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            autoFocus
            placeholder="Pesquisar cliente (nome / telefone / email / NIF)…"
            value={customerPickerQuery}
            onChange={(e) => setCustomerPickerQuery(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-slate-100"
          />
        </div>

        <div className="max-h-[55vh] overflow-auto rounded-lg border border-slate-800">
          <div className="p-2">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-slate-700 bg-slate-950 hover:bg-slate-800 text-slate-200"
              onClick={() => {
                setOrderForm((prev) => ({
                  ...prev,
                  customer_id: '',
                  customer_name: '',
                  customer_phone: '',
                  customer_email: '',
                  payment_phone: '',
                }));
                setCustomerPickerOpen(false);
              }}
            >
              Inserir manualmente (Sem cliente associado)
            </Button>
          </div>

          <div className="p-2 space-y-2">
            {filteredCustomerOptions.map((c) => (
              <button
                key={c.id}
                type="button"
                className="w-full text-left rounded-xl border border-slate-800 bg-slate-950 hover:bg-slate-800 px-4 py-3 transition"
                onClick={() => {
                  setOrderForm((prev) => ({
                    ...prev,
                    customer_id: c.id,
                    customer_name: c.name || '',
                    customer_phone: c.phone || '',
                    customer_email: c.email || '',
                    payment_phone: c.phone || '',
                  }));
                  setCustomerPickerOpen(false);
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate">{c.name || '(Sem nome)'}</div>
                    <div className="text-xs text-slate-300 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      <span>Tel: <span className="text-slate-100">{c.phone || '—'}</span></span>
                      <span>Email: <span className="text-slate-100">{c.email || '—'}</span></span>
                      <span>NIF: <span className="text-slate-100">{c.nif || '—'}</span></span>
                    </div>
                  </div>
                </div>
              </button>
            ))}

            {filteredCustomerOptions.length === 0 && (
              <div className="text-center py-10 text-slate-500">
                Nenhum cliente encontrado para esta pesquisa.
              </div>
            )}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          className="border-slate-700"
          onClick={() => setCustomerPickerOpen(false)}
        >
          Fechar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</div>

            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Nome do Cliente</Label>
                <Input
                  value={orderForm.customer_name}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, customer_name: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label className="text-slate-300">Telefone</Label>
                <Input
                  value={orderForm.customer_phone}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={orderForm.customer_email}
                onChange={(e) => setOrderForm(prev => ({ ...prev, customer_email: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Para notificação por email"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Artigos da Encomenda</Label>
                <Button type="button" variant="outline" className="border-slate-700" onClick={addOrderItem}>
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar produto
                </Button>
              </div>

              <div className="space-y-3">
                {(orderForm.items || []).map((item, index) => (
                  <div key={index} className="rounded-xl border border-slate-700 bg-slate-800/40 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-200 font-medium">Produto {index + 1}</div>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-slate-700"
                        onClick={() => removeOrderItem(index)}
                        disabled={(orderForm.items || []).length <= 1}
                      >
                        Remover
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={itemPickerQueries[index] ?? item.product_name ?? ''}
                          onFocus={() => setActiveItemPickerIndex(index)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setItemPickerQueries((prev) => ({ ...prev, [index]: value }));
                            updateOrderItem(index, { product_name: value, product_id: item.product_id || '' });
                            setActiveItemPickerIndex(index);
                          }}
                          className="bg-slate-800 border-slate-700"
                          placeholder="Pesquisar produto por nome, referência ou código..."
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="border-slate-700 bg-slate-800"
                          onClick={() => setActiveItemPickerIndex(activeItemPickerIndex === index ? null : index)}
                        >
                          Escolher
                        </Button>
                      </div>

                      {activeItemPickerIndex === index && (
                        <div className="max-h-48 overflow-auto rounded-lg border border-slate-700 bg-slate-800/80">
                          <div className="p-2 space-y-2">
                            {getFilteredProductOptionsForItem(index).map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                className="w-full text-left rounded-lg border border-slate-700 bg-slate-900 hover:bg-slate-700 px-3 py-2 transition"
                                onClick={() => selectProductForOrderItem(index, p.id)}
                              >
                                <div className="text-white font-medium truncate">{p.name}</div>
                                <div className="text-xs text-slate-300 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                  <span>Ref: <span className="text-slate-100">{p.reference || '—'}</span></span>
                                  <span>Código: <span className="text-slate-100">{p.barcode || '—'}</span></span>
                                  <span>PVP: <span className="text-slate-100">€{Number(p.sell_price || 0).toFixed(2)}</span></span>
                                </div>
                              </button>
                            ))}

                            {getFilteredProductOptionsForItem(index).length === 0 && (
                              <div className="text-center py-6 text-slate-400">
                                Nenhum produto encontrado para esta pesquisa.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Nome do Produto</Label>
                        <Input
                          value={item.product_name}
                          onChange={(e) => updateOrderItem(index, { product_name: e.target.value })}
                          className="bg-slate-800 border-slate-700"
                        />
                      </div>

                      <div>
                        <Label className="text-slate-300">Quantidade</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          inputMode="numeric"
                          value={item.quantity}
                          onChange={(e) => updateOrderItem(index, { quantity: parseInt(e.target.value) || 1 })}
                          className="bg-slate-800 border-slate-700"
                        />
                      </div>

                      <div>
                        <Label className="text-slate-300">Preço unitário (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateOrderItem(index, { unit_price: Number(String(e.target.value || 0).replace(',', '.')) })}
                          className="bg-slate-800 border-slate-700"
                        />
                      </div>

                      <div>
                        <Label className="text-slate-300">Desconto</Label>
                        <Select
                          value={String(item.discount_percent ?? 0)}
                          onValueChange={(val) => updateOrderItem(index, { discount_percent: Number(val) })}
                        >
                          <SelectTrigger className="bg-slate-800 border-slate-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0%</SelectItem>
                            <SelectItem value="5">5%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="15">15%</SelectItem>
                            <SelectItem value="20">20%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="text-xs text-slate-400">
                      Subtotal da linha: <span className="text-white font-medium">€{Number(orderTotals.items[index]?.line_subtotal || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Portes de envio (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={orderForm.shipping_cost}
                    onChange={(e) => setOrderForm((prev) => ({ ...prev, shipping_cost: Number(String(e.target.value || 0).replace(',', '.')) }))}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-white">€{Number(orderTotals.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Desconto total</span>
                    <span className="text-white">€{Number(orderTotals.discountTotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Portes</span>
                    <span className="text-white">€{Number(orderTotals.shippingCost || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold pt-2 border-t border-slate-800">
                    <span className="text-slate-200">Total</span>
                    <span className="text-emerald-400">€{Number(orderTotals.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Data Prevista de Chegada</Label>
              <Input
                type="date"
                min={todayDateValue}
                max={maximumEstimatedArrival}
                value={orderForm.estimated_arrival}
                onChange={(e) => setOrderForm(prev => ({ ...prev, estimated_arrival: e.target.value }))}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-slate-200">Pré-pagamento</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Ativa se quiseres cobrar antes do levantamento.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setOrderForm(prev => ({
                    ...prev,
                    payment_required: !prev.payment_required,
                    payment_method: !prev.payment_required ? (prev.payment_method || 'mbway') : '',
                    payment_amount: !prev.payment_required ? prev.payment_amount : '',
                    payment_phone: !prev.payment_required ? (prev.payment_phone || prev.customer_phone || '') : '',
                    payment_notes: !prev.payment_required ? prev.payment_notes : '',
                  }))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    orderForm.payment_required
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-800 text-slate-300 border border-slate-700'
                  }`}
                >
                  {orderForm.payment_required ? 'Ativado' : 'Desativado'}
                </button>
              </div>

              {orderForm.payment_required && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Método</Label>
                      <Select
                        value={orderForm.payment_method || 'mbway'}
                        onValueChange={(val) => setOrderForm(prev => ({ ...prev, payment_method: val }))}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mbway">MB WAY</SelectItem>
                          <SelectItem value="multibanco">Referência Multibanco</SelectItem>
                          <SelectItem value="cash">Dinheiro</SelectItem>
                          <SelectItem value="card">Cartão</SelectItem>
                          <SelectItem value="voucher">Voucher</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-slate-300">Valor a cobrar (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={orderForm.payment_amount}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, payment_amount: e.target.value }))}
                        className="bg-slate-800 border-slate-700"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {orderForm.payment_method === 'mbway' && (
                    <div>
                      <Label className="text-slate-300">Telefone MB WAY</Label>
                      <Input
                        value={orderForm.payment_phone}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, payment_phone: e.target.value }))}
                        className="bg-slate-800 border-slate-700"
                        placeholder="+351..."
                      />
                    </div>
                  )}

                  <div>
                    <Label className="text-slate-300">Notas de pagamento</Label>
                    <Input
                      value={orderForm.payment_notes}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, payment_notes: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                      placeholder="Ex.: sinal, reserva, pago no balcão..."
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <Label className="text-slate-300">Notas</Label>
              <Input
                value={orderForm.notes}
                onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                placeholder="Observações..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={createOrder} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Criar Encomenda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Payment Result Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Dados de Pagamento</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-slate-800">
                <p className="text-sm text-slate-400">Encomenda</p>
                <p className="text-white font-medium">{selectedOrder.order_number}</p>
                <p className="text-emerald-400">{selectedOrder.product_name}</p>
              </div>

              <div className="p-4 rounded-lg bg-slate-800 space-y-2">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-400">Método</span>
                  <span className="text-white font-medium uppercase">{selectedOrder.payment_method || '—'}</span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-slate-400">Valor</span>
                  <span className="text-white font-medium">€{Number(selectedOrder.payment_amount || 0).toFixed(2)}</span>
                </div>

                {selectedOrder.payment_phone && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400">Telefone</span>
                    <span className="text-white font-medium">{selectedOrder.payment_phone}</span>
                  </div>
                )}

                {selectedOrder.payment_reference_entity && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400">Entidade</span>
                    <span className="text-white font-medium">{selectedOrder.payment_reference_entity}</span>
                  </div>
                )}

                {selectedOrder.payment_reference && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400">Referência</span>
                    <span className="text-white font-medium">{selectedOrder.payment_reference}</span>
                  </div>
                )}

                {selectedOrder.payment_request_id && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400">Request ID</span>
                    <span className="text-white font-medium text-xs break-all text-right">{selectedOrder.payment_request_id}</span>
                  </div>
                )}
              </div>

              <div className="text-xs text-slate-400">
                O estado da encomenda será atualizado após confirmação da Ifthenpay pelo callback.
              </div>


              {selectedOrder.payment_method === 'multibanco' &&
                selectedOrder.payment_reference_entity &&
                selectedOrder.payment_reference &&
                selectedOrder.payment_status !== 'paid' && (
                  <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3 space-y-3">
                    <div className="text-sm text-slate-200 font-medium">
                      Notificar cliente com os dados de pagamento
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <Label className="text-xs text-slate-400">Telemóvel para SMS</Label>
                        <Input
                          value={paymentNotifyForm.phone}
                          onChange={(e) => setPaymentNotifyForm((prev) => ({ ...prev, phone: e.target.value }))}
                          className="bg-slate-900 border-slate-700 text-white mt-1"
                          placeholder="Ex.: 926550649"
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-slate-400">Email para envio</Label>
                        <Input
                          value={paymentNotifyForm.email}
                          onChange={(e) => setPaymentNotifyForm((prev) => ({ ...prev, email: e.target.value }))}
                          className="bg-slate-900 border-slate-700 text-white mt-1"
                          placeholder="cliente@email.pt"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 hover:bg-slate-700 text-white w-full sm:w-auto"
                        disabled={notifyingPayment || !String(paymentNotifyForm.phone || '').trim()}
                        onClick={() => handleSendPaymentNotification('sms')}
                      >
                        {notifyingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                        Enviar SMS
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 hover:bg-slate-700 text-white w-full sm:w-auto"
                        disabled={notifyingPayment || !String(paymentNotifyForm.email || '').trim()}
                        onClick={() => handleSendPaymentNotification('email')}
                      >
                        {notifyingPayment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                        Enviar Email
                      </Button>
                    </div>

                    <p className="text-[11px] text-slate-500">
                      Estes contactos são usados apenas para este envio e não alteram a ficha do cliente.
                    </p>
                  </div>
                )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-slate-700"
              onClick={() => setShowPaymentModal(false)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify Customer Modal */}
      <Dialog open={showNotifyModal} onOpenChange={setShowNotifyModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Notificar Cliente</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-400">Encomenda</p>
                <p className="text-white font-medium">{selectedOrder.order_number}</p>
                <p className="text-emerald-400">{selectedOrder.product_name}</p>
              </div>

              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-400 mb-2">Cliente</p>
                <p className="text-white font-medium">{selectedOrder.customer_name}</p>
                {selectedOrder.customer_phone && (
                  <p className="text-sm text-slate-400">Tel: {selectedOrder.customer_phone}</p>
                )}
                {selectedOrder.customer_email && (
                  <p className="text-sm text-slate-400">Email: {selectedOrder.customer_email}</p>
                )}
              </div>

              <div className="p-4 bg-slate-800 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <p className="text-sm text-slate-300 font-medium">Auditoria da notificação</p>
                </div>

                <div className="text-sm text-slate-400 space-y-1">
                  <p>Canal registado: <span className="text-white">{getNotificationChannelLabel(selectedOrder)}</span></p>
                  <p>Data: <span className="text-white">{formatDateTime(selectedOrder.notification_date)}</span></p>

                  {selectedOrder.email_sent && (
                    <p>Email: <span className="text-white">{selectedOrder.email_sent_to || 'enviado'}</span></p>
                  )}

                  {selectedOrder.sms_sent && (
                    <p>SMS: <span className="text-white">{selectedOrder.sms_sent_to || 'enviado'}</span></p>
                  )}

                  {selectedOrder.sms_message_id && (
                    <p>Closum message_id: <span className="text-white break-all">{selectedOrder.sms_message_id}</span></p>
                  )}

                  {selectedOrder.sms_text && (
                    <div className="pt-2">
                      <p className="mb-1">Texto SMS:</p>
                      <div className="text-xs text-slate-300 bg-slate-900 rounded p-3 leading-relaxed">
                        {selectedOrder.sms_text}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-sm text-slate-400">Selecione o método de notificação:</p>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleNotifyCustomer('email')}
                  disabled={saving || !selectedOrder.customer_email}
                  className="border-slate-700 h-20 flex-col"
                >
                  <Mail className="w-6 h-6 mb-2" />
                  Email
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleNotifyCustomer('sms')}
                  disabled={saving || !selectedOrder.customer_phone}
                  className="border-slate-700 h-20 flex-col"
                >
                  <MessageSquare className="w-6 h-6 mb-2" />
                  SMS
                </Button>
              </div>

              {(!selectedOrder.customer_email && !selectedOrder.customer_phone) && (
                <p className="text-sm text-amber-400 text-center">
                  Cliente sem email ou telefone registado
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotifyModal(false)} className="border-slate-700">
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerProductOrdersPage;
